/**
 * Floating call dock rendered globally by AppShell.
 *
 * Two views:
 *   - Incoming call banner (candidate calling +33 1 62 29 09 54)
 *   - Active call dock (either accepted-inbound or outbound-in-progress)
 *
 * Uses Framer Motion for enter/exit and stays visible on every page.
 */
import { motion, AnimatePresence } from "framer-motion";
import { Mic, MicOff, Phone, PhoneIncoming, PhoneOff } from "lucide-react";
import { useEffect, useState } from "react";

import { useCallCenter } from "../../lib/callCenter";
import { toE164 } from "../../lib/format";

function formatDuration(sec) {
    if (!Number.isFinite(sec) || sec < 0) return "00:00";
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function CallDock() {
    const { activeCall, incomingCall, hangup, acceptIncoming, rejectIncoming, toggleMute, muted, status } = useCallCenter();
    const [tick, setTick] = useState(0);

    useEffect(() => {
        if (!activeCall) return undefined;
        const id = setInterval(() => setTick((t) => t + 1), 1000);
        return () => clearInterval(id);
    }, [activeCall]);

    const elapsed = activeCall && status === "active"
        ? Math.floor((Date.now() - activeCall.startedAt) / 1000)
        : 0;

    return (
        <AnimatePresence>
            {incomingCall && !activeCall && (
                <motion.div
                    key="incoming"
                    data-testid="call-dock-incoming"
                    initial={{ y: -80, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -80, opacity: 0 }}
                    transition={{ type: "spring", stiffness: 250, damping: 22 }}
                    className="fixed top-4 right-4 z-[60] w-[340px] rounded-2xl border-2 border-emerald-300 bg-white shadow-2xl overflow-hidden"
                >
                    <div className="px-4 py-3 bg-gradient-to-br from-emerald-500 to-emerald-600 text-white flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center">
                            <PhoneIncoming className="h-5 w-5 animate-pulse" />
                        </div>
                        <div>
                            <div className="text-[10px] font-black uppercase tracking-widest opacity-90">Appel entrant</div>
                            <div className="font-extrabold">{toE164(incomingCall.from) || incomingCall.from}</div>
                        </div>
                    </div>
                    <div className="p-3 flex gap-2">
                        <button
                            type="button"
                            onClick={acceptIncoming}
                            data-testid="call-dock-accept"
                            className="flex-1 h-11 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-extrabold text-sm inline-flex items-center justify-center gap-2 shadow-md transition"
                        >
                            <Phone className="h-4 w-4" /> Répondre
                        </button>
                        <button
                            type="button"
                            onClick={rejectIncoming}
                            data-testid="call-dock-reject"
                            className="h-11 px-4 rounded-xl bg-slate-100 hover:bg-red-100 text-slate-700 hover:text-red-700 font-extrabold text-sm inline-flex items-center justify-center gap-2 transition"
                        >
                            <PhoneOff className="h-4 w-4" />
                        </button>
                    </div>
                </motion.div>
            )}

            {activeCall && (
                <motion.div
                    key="active"
                    data-testid="call-dock-active"
                    initial={{ y: 80, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 80, opacity: 0 }}
                    transition={{ type: "spring", stiffness: 250, damping: 22 }}
                    className="fixed bottom-6 right-6 z-[60] w-[320px] rounded-2xl border-2 border-shariti-orange/40 bg-shariti-black text-white shadow-2xl overflow-hidden"
                >
                    <div className="px-4 py-3 bg-gradient-to-br from-shariti-orange to-[#d66528] text-white">
                        <div className="text-[10px] font-black uppercase tracking-widest opacity-90 flex items-center gap-1.5">
                            <span className="h-2 w-2 rounded-full bg-white animate-pulse" />
                            {status === "ringing" && "Sonnerie…"}
                            {status === "connecting" && "Connexion…"}
                            {status === "active" && `En communication · ${formatDuration(elapsed)}`}
                            {status === "ended" && "Terminé"}
                        </div>
                        <div className="mt-1 font-extrabold text-base leading-tight truncate">
                            {activeCall.name || activeCall.phone}
                        </div>
                        {activeCall.name && (
                            <div className="text-xs font-semibold opacity-90 truncate">{activeCall.phone}</div>
                        )}
                    </div>
                    <div className="p-3 flex items-center gap-2">
                        <button
                            type="button"
                            onClick={toggleMute}
                            data-testid="call-dock-mute"
                            title={muted ? "Réactiver le micro" : "Couper le micro"}
                            className={`h-11 w-11 rounded-full inline-flex items-center justify-center font-bold transition ${
                                muted ? "bg-amber-500 text-white" : "bg-white/10 hover:bg-white/20 text-white"
                            }`}
                        >
                            {muted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                        </button>
                        <button
                            type="button"
                            onClick={hangup}
                            data-testid="call-dock-hangup"
                            className="flex-1 h-11 rounded-xl bg-shariti-red hover:bg-red-700 text-white font-extrabold text-sm inline-flex items-center justify-center gap-2 shadow-md transition"
                        >
                            <PhoneOff className="h-4 w-4" /> Raccrocher
                        </button>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
